import React, { useEffect, useState } from 'react';
import FlashCardAnimation from './FlashCardAnimation';
import FlashCardStack from './FlashCardStack';
import { Exercise } from '@/models/LearnTypes';
import { cn } from '@/lib/utils';
import { ANIMATION_TIMINGS } from '@/constants/animations';
import { AnimationState } from '@/hooks/useFlashCardManager';

interface FlashCardDisplayProps {
  // Core content props
  stackedCards: Exercise[];
  frontContent: string;
  backContent: string;
  currentExerciseId: string;
  isFlipped: boolean;
  onFlip: () => void;
  
  // Animation system props
  /**
   * Current animation state from useFlashCardManager hook
   * This drives all animations and transitions for the card
   */
  animationState: AnimationState;
}

/**
 * Component that renders the flashcard stack and active card
 * 
 * This component:
 * 1. Displays the current active card that the user interacts with
 * 2. Shows a stack of cards underneath representing remaining cards
 * 3. Handles multiple animation transitions based on AnimationState enum:
 *    - AnimationState.IDLE: Standard display state
 *    - AnimationState.FLIPPING: Card flip animations
 *    - AnimationState.EXCELLENT: Positive feedback animations
 *    - AnimationState.EXITING: Card swipe/exit animations
 *    - AnimationState.RETURNING: Card return to stack animations
 * 
 * All animation state is controlled by the AnimationState enum passed from parent components
 */
const FlashCardDisplay: React.FC<FlashCardDisplayProps> = ({
  // Core content props
  stackedCards,
  frontContent,
  backContent,
  currentExerciseId,
  isFlipped,
  onFlip,
  
  // Animation system props
  animationState
}) => {
  // Control visibility of the active card - always visible by default
  const [showActiveCard, setShowActiveCard] = useState(true);
  
  // Track whether the card is currently being flipped
  const [isFlipping, setIsFlipping] = useState(false);
  
  // Sync with animation state changes
  useEffect(() => {
    switch (animationState) {
      case AnimationState.FLIPPING:
        setIsFlipping(true);
        // Reset after flip animation
        const timer = setTimeout(() => {
          setIsFlipping(false);
        }, ANIMATION_TIMINGS.FLIP_DURATION);
        
        return () => clearTimeout(timer);
      
      case AnimationState.EXITING:
        // Ensure card remains visible during exit animation
        setShowActiveCard(true);
        
        // After animation completes, hide the card
        const exitTimer = setTimeout(() => {
          setShowActiveCard(false);
        }, ANIMATION_TIMINGS.SWIPE_DURATION);
        
        return () => clearTimeout(exitTimer);
        
      case AnimationState.EXCELLENT:
        // Handle excellent animation
        break;
    }
  }, [animationState]);
  
  // Always show the active card when the current exercise changes
  useEffect(() => {
    setShowActiveCard(true);
  }, [currentExerciseId]);
  
  // Track flip state changes to temporarily adjust z-index during flipping
  // Only respond to explicit flip actions, not animations
  useEffect(() => {
    // Skip this effect during excellent animation to avoid the transparency flicker
    if (animationState === AnimationState.EXCELLENT) return;
    
    // Set flipping immediately without animation delay
    setIsFlipping(true);
    const timer = setTimeout(() => {
      setIsFlipping(false);
    }, 100); // Much shorter duration for minimal visual interruption
    
    return () => clearTimeout(timer);
  }, [isFlipped, animationState]);

  /**
   * Map the animation state to the appropriate CSS animation class
   */
  const getAnimationClass = () => {
    switch (animationState) {
      case AnimationState.EXITING:
        // Use the swipe-right animation from the keyframes defined in CSS
        // The animation-fill-mode: forwards ensures it stays in the final position
        return 'animate-[swipe-right_250ms_ease-in-out_forwards]';
      case AnimationState.RETURNING:
        return 'animate-bounce-once';
      default:
        return '';
    }
  };

  return (
    <div className="relative w-full h-72 pt-2 mb-4"> 
      {/* Stacked cards container */}
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Render stack of cards behind the active card */}
        <div className="relative w-full h-full max-w-md mx-auto perspective-1500">
          {/* The card stack container - position it more visibly */}
          <div className="absolute inset-0 flex items-center justify-center">
            <FlashCardStack 
              exercises={stackedCards.slice(1)}
              currentIndex={0}
              // Use animation state to derive stack behavior
              initialLoad={false}
              deckAnimationActive={animationState === AnimationState.RETURNING}
              isNextCardOnly={isFlipped} // Only show next card when flipped
              showAsTopCard={isFlipped && animationState === AnimationState.EXITING} // Only promote next card when current is being swiped away
            />
          </div>
          
          {/* Render active card - positioned to match stack positioning */}
          <div 
            className={cn(
              "absolute",
              "card-shadow-md",
              showActiveCard ? "opacity-100" : "opacity-0",
              getAnimationClass() // Use the updated animation class getter
            )}
            style={{
              width: '87%',   // Match width from previous update
              height: '87%',  // Match height from previous update
              left: '5%',     // Left offset
              top: '4%',      // Top offset
              transform: 'rotate(-1deg)', // Keep the slight rotation
              zIndex: 50,
              opacity: showActiveCard ? 1 : 0 // Simple opacity change with no animation
            }}
          >
            <FlashCardAnimation
              onFlip={onFlip}
              isFlipped={isFlipped}
              frontContent={frontContent}
              backContent={backContent}
              currentExerciseId={currentExerciseId}
              animationState={animationState}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashCardDisplay;
